import React from "react";
import {Alert, Skeleton, Tab, Tooltip} from "@mui/material";
import {ArrowBack, DescriptionOutlined, ListAltOutlined, PaidOutlined, SavingsOutlined} from "@mui/icons-material";
import {CustomTabs} from "~/components/tabs/Tabs.tsx";
import {Tag} from "~/components/tag/Tag.tsx";
import type {ContractTab} from "./useContractListParams.ts";
import type {LinkStatus} from "./contractExplorerTypes.ts";
import {formatDate, typeLabel} from "./contractExplorerFormat.ts";
import {useContractDetailQuery} from "./useContractDetailQuery.ts";
import {DetailsTab} from "./components/DetailsTab.tsx";
import {LifecycleTag} from "./components/LifecycleTag.tsx";
import {OpportunitiesTab} from "./components/OpportunitiesTab.tsx";
import {SpendTab} from "./components/SpendTab.tsx";

type Props = {
    bagId: number;
    contractId: number;
    tab: ContractTab;
    links: LinkStatus;
    onTab: (tab: ContractTab) => void;
    onLinks: (status: LinkStatus) => void;
    onOpenContract: (contractId: number) => void;
    onBack: () => void;
};

/** One bundle: its header, then the Details, Spend under contract and Opportunities tabs. Read-only. */
export const ContractDetailView: React.FC<Props> = ({bagId, contractId, tab, links, onTab, onLinks, onOpenContract, onBack}) => {
    const {data: contract, isPending, error} = useContractDetailQuery(bagId, contractId);

    const back = (
        <button type="button" className="ci-back" onClick={onBack}>
            <ArrowBack fontSize="small"/> Contract Intelligence
        </button>
    );
    if (error) {
        return <>{back}<Alert severity="error">This bundle could not be loaded: {error.message}</Alert></>;
    }
    if (isPending || !contract) {
        return <>{back}<div className="ci-dhead"><Skeleton width="40%" height={44}/><Skeleton width="60%"/></div>
            <Skeleton variant="rounded" height={240}/></>;
    }
    const buyers = contract.buying_entities.length;
    const findings = contract.opportunity_counts.reduce((n, c) => n + c.findings, 0);
    let body: React.ReactNode;
    switch (tab) {
        case 'spend':
            body = <SpendTab bagId={bagId} contract={contract} status={links} onStatus={onLinks}
                             onOpenContract={onOpenContract}/>;
            break;
        case 'opportunities':
            body = <OpportunitiesTab key={contract.id} bagId={bagId} contract={contract}/>;
            break;
        default:
            body = <DetailsTab key={contract.id} contract={contract}/>;
    }
    return <>
        {back}
        <header className="ci-dhead">
            <div className="ci-badges">
                <LifecycleTag lifecycle={contract.lifecycle}/>
                <Tooltip arrow title={contract.contract_type ? `Subject: ${typeLabel(contract.contract_type)}` : ''}>
                    <span><Tag label={typeLabel(contract.document_type)} color="default" variant="outlined"/></span>
                </Tooltip>
                <Tag icon={<DescriptionOutlined/>} color="default" variant="outlined"
                     label={`${contract.document_count} source ${contract.document_count === 1 ? 'document' : 'documents'}`}/>
                <Tag color="default" variant="outlined"
                     label={`${contract.version_count} ${contract.version_count === 1 ? 'version' : 'versions'}`}/>
                {contract.currency && contract.currency !== contract.spend_currency && /^[A-Z]{3}$/.test(contract.currency) && (
                    <Tooltip arrow title={`The lines are priced in ${contract.currency}; spend is in ${contract.spend_currency}.`}>
                        <span><Tag label={contract.currency} color="default" variant="outlined"/></span>
                    </Tooltip>
                )}
                <span className="ci-id">#{contract.bundle_id}</span>
            </div>
            <h1 className="ci-dtitle">{contract.title}</h1>
            <div className="ci-dmeta">
                <b>{contract.supplier_name || 'No supplier'}</b>
                <span className="sep">·</span>
                <span>{buyers ? `${buyers} buying ${buyers === 1 ? 'entity' : 'entities'}` : 'No buying entity'}</span>
                <span className="sep">·</span>
                <span>{formatDate(contract.starts_on)} → {contract.ends_on ? formatDate(contract.ends_on) : 'no end date'}</span>
                {contract.label && contract.label !== contract.title && <>
                    <span className="sep">·</span><span title={contract.folder}>{contract.label}</span>
                </>}
            </div>
        </header>
        <CustomTabs className="ci-tabs" value={tab} onChange={(_e, next: ContractTab) => onTab(next)}>
            <Tab value="details" icon={<ListAltOutlined fontSize="small"/>} iconPosition="start" label="Details"/>
            <Tab value="spend" icon={<PaidOutlined fontSize="small"/>} iconPosition="start"
                 label={`Spend under contract (${contract.link_counts.linked})`}/>
            <Tab value="opportunities" icon={<SavingsOutlined fontSize="small"/>} iconPosition="start"
                 label={`Opportunities (${findings})`}/>
        </CustomTabs>
        <div className="ci-body">{body}</div>
    </>;
};
